import { Box, Card, Checkbox, Input, Radio, RadioGroup, Stack, Typography } from '@mui/joy';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'chatbot/components';
import { ChatbotQuestionType, IChatbotQuestion } from 'chatbot/types';
import { Direction, Level } from 'theme/enums';

interface IProps {
  title?: string;
  questions: IChatbotQuestion[];
  onSubmit?: (questions: IChatbotQuestion[]) => void;
}

export const ConversationQuestions = ({ title, questions: initialQuestions, onSubmit }: IProps) => {
  const [questions, setQuestions] = useState(initialQuestions);

  const { t } = useTranslation();

  const isFullyAnswered = questions.every((question) =>
    Array.isArray(question.answer) ? question.answer.length > 0 : !!question.answer
  );

  const onAnswerChange = (questionIndex: number, answer: string | string[]) => {
    const newQuestions = [...questions];
    newQuestions[questionIndex].answer = answer;
    setQuestions(newQuestions);
  };

  return (
    <Stack gap={2}>
      <Typography level={Level.TITLE_MD}>{title}</Typography>
      <Stack direction={Direction.COLUMN} spacing={1}>
        {questions.map((question, index) => (
          <ConversationQuestion
            key={question.id}
            question={question}
            onChange={(answer) => onAnswerChange(index, answer)}
            disabled={!onSubmit}
          />
        ))}
      </Stack>
      {onSubmit && (
        <Button
          disabled={!isFullyAnswered}
          onClick={() => onSubmit(questions)}
          sx={{ width: 'fit-content' }}
        >
          {t('buttonLabelApply')}
        </Button>
      )}
    </Stack>
  );
};

const ConversationQuestion = ({
  question,
  onChange,
  disabled,
}: {
  question: IChatbotQuestion;
  onChange: (answer: string | string[]) => void;
  disabled: boolean;
}) => {
  return (
    <Card>
      <Typography level={Level.BODY_MD}>{question.title}</Typography>
      <Typography level={Level.BODY_SM}>{question.description}</Typography>
      <Box sx={{ mt: 2 }}>
        {question.type === ChatbotQuestionType.TEXT && (
          <TextQuestion question={question} onChange={onChange} disabled={disabled} />
        )}
        {question.type === ChatbotQuestionType.SINGLE_CHOICE && (
          <SingleChoiceQuestion question={question} onChange={onChange} disabled={disabled} />
        )}
        {question.type === ChatbotQuestionType.MULTIPLE_CHOICE && (
          <MultipleChoiceQuestion question={question} onChange={onChange} disabled={disabled} />
        )}
      </Box>
    </Card>
  );
};

const TextQuestion = ({
  question,
  onChange,
  disabled,
}: {
  question: IChatbotQuestion;
  onChange: (answer: string) => void;
  disabled: boolean;
}) => {
  return (
    <Input
      placeholder={question.description}
      sx={{ width: '100%' }}
      value={question.answer || ''}
      onChange={(e) => onChange(e.target.value)}
      disabled={disabled}
    />
  );
};

const SingleChoiceQuestion = ({
  question,
  onChange,
  disabled,
}: {
  question: IChatbotQuestion;
  onChange: (answer: string[]) => void;
  disabled: boolean;
}) => {
  return (
    <RadioGroup value={question.answer?.[0] || ''} onChange={(e) => onChange([e.target.value])}>
      {question.options?.map((option) => (
        <Radio key={option} label={option} value={option} disabled={disabled} />
      ))}
    </RadioGroup>
  );
};

const MultipleChoiceQuestion = ({
  question,
  onChange,
  disabled,
}: {
  question: IChatbotQuestion;
  onChange: (answer: string[]) => void;
  disabled: boolean;
}) => {
  return (
    <Stack direction={Direction.COLUMN} spacing={2}>
      {question.options?.map((option) => (
        <Checkbox
          key={option}
          label={option}
          checked={Array.isArray(question.answer) && question.answer.includes(option)}
          onChange={(e) => {
            const options = Array.isArray(question.answer) ? question.answer : [];
            onChange(e.target.checked ? [...options, option] : options.filter((o) => o !== option));
          }}
          disabled={disabled}
        />
      ))}
    </Stack>
  );
};
